import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, share, switchMap, take } from 'rxjs/operators';
import { App } from '../../../interfaces/app';
import { IPreparedFile } from '../../../services/file-upload.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@UntilDestroy()
@Component({
  selector: 'app-config-form',
  templateUrl: './app-config-form.component.html',
  styleUrls: ['./app-config-form.component.scss'],
})
export class AppConfigFormComponent implements OnInit {

  @Input()
  public app: App | undefined = undefined;

  @Input()
  public set config(value: unknown) {
    this.configSubject.next(value);
  }

  @Input()
  public set files(value: IPreparedFile[]) {
    this.filesSubject.next(value);
  }

  @Output()
  public configChange = new EventEmitter<unknown>();

  @Output()
  public openFileManager = new EventEmitter<void>();

  @ViewChild('iframe')
  public iframe: ElementRef<HTMLIFrameElement> | undefined;

  public url: SafeResourceUrl | undefined = undefined;
  public height = 600;

  public configSubject = new BehaviorSubject<unknown>(undefined);
  public filesSubject = new BehaviorSubject<IPreparedFile[]>([]);

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
  ) {}

  public ngOnInit(): void {
    if (this.app?.configFormUrl) {
      this.url = this.sanitizer.bypassSecurityTrustResourceUrl(this.app.configFormUrl);
      this.changeDetectorRef.detectChanges();
    }
  }

  public onIframeLoad(): void {
    const channel = new MessageChannel();
    channel.port1.start();

    setTimeout(() => {
      this.iframe?.nativeElement.contentWindow?.postMessage('oongalee-studio-handshake', '*', [channel.port2]);
    }, 100);

    const sendMessage = (event: string, data: any) => {
      channel.port1.postMessage({
        type: 'oongalee-studio-event',
        version: '0.0.1',
        event,
        data,
      });
    };
    const message$ = fromEvent<MessageEvent>(channel.port1, 'message').pipe(
      map((event) => event.data),
      share(),
    );

    // Init
    const init$ = message$.pipe(
      filter((message) => message.event === 'connected'),
      take(1),
    );

    init$.pipe(
      switchMap(() => this.configSubject.pipe(take(1))),
      map((config) => config || this.app?.configDefault),
      untilDestroyed(this),
    ).subscribe((config) => {
      sendMessage('config', config);
    });

    init$.pipe(
      switchMap(() => this.filesSubject),
      untilDestroyed(this),
    ).subscribe((files) => {
      sendMessage('files', files);
    });

    // Resize
    message$.pipe(
      filter((message) => message.event === 'resize'),
      map((message) => message.data.height),
      untilDestroyed(this),
    ).subscribe((height) => {
      this.height = height;
      this.changeDetectorRef.detectChanges();
    });

    // Open file manager
    message$.pipe(
      filter((message) => message.event === 'files.manager.open'),
      untilDestroyed(this),
    ).subscribe(() => {
      console.log('Emit open file manager');
      this.openFileManager.emit();
    });

    // Config change
    message$.pipe(
      filter((message) => message.event === 'config.change'),
      map((message) => message.data),
      // debounceTime(100),
      untilDestroyed(this),
    ).subscribe((newConfig) => {
      this.configChange.emit(newConfig);
    });
  }

}
